import {
    Alert,
    Box,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography,
    Button
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import VideocamIcon from '@mui/icons-material/Videocam';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import SettingsIcon from '@mui/icons-material/Settings';
import CallEndIcon from '@mui/icons-material/CallEnd';
import CallOutlinedIcon from '@mui/icons-material/CallOutlined';
import PersonIcon from '@mui/icons-material/Person';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { callConfig } from '../configs/call';
import { useCallManager } from '../call';
import { useUser } from '../hooks/useUser';
import { fetchUserByRole } from '../services/users';
import { createClinicalNote, fetchClinicalNote, listClinicalNotes } from '../services/clinicalNotes';
import { createCallLog, sendCallPush, updateCallLog } from '../services/calls';
import { updateAppointmentAdmin } from '../services/appointments';
import { listPatients } from '../services/patients';
import { listMeasurements } from '../services/measurements';
import { listDocuments, downloadDocument } from '../services/documents';

const shellCardSx = {
    borderRadius: '22px',
    bgcolor: '#FFFFFF',
    boxShadow: 'none'
};

const dialogPaperSx = {
    borderRadius: '22px',
    boxShadow: '0px 10px 30px rgba(61, 61, 61, 0.12)'
};

const dialogTitleSx = {
    px: 3,
    pt: 3,
    pb: 1.5,
    fontWeight: 700,
    color: '#3D3D3D'
};

const dialogContentSx = {
    px: 3,
    pb: 1,
    pt: 0.5
};

const dialogActionsSx = {
    px: 3,
    pb: 2.5,
    pt: 1
};

const secondaryButtonSx = {
    minHeight: 46,
    px: 2.2,
    textTransform: 'none',
    fontWeight: 700,
    color: '#3D3D3D',
    borderColor: '#D9D9D9',
    borderWidth: '2px',
    borderRadius: '16px',
    '&:hover': {
        bgcolor: '#F7F7F7',
        borderColor: '#CFCFCF',
        borderWidth: '2px'
    }
};

const primaryButtonSx = {
    minHeight: 46,
    px: 2.4,
    textTransform: 'none',
    fontWeight: 700,
    borderRadius: '16px',
    bgcolor: '#3D3D3D',
    boxShadow: 'none',
    '&:hover': {
        bgcolor: '#2F2F2F',
        boxShadow: 'none'
    }
};

const controlButtonSx = {
    width: 52,
    height: 52,
    bgcolor: '#F7F7F7',
    border: '1px solid #E2E2E2',
    color: '#3D3D3D',
    '&:hover': {
        bgcolor: '#ECECEC'
    }
};

const activeControlButtonSx = {
    ...controlButtonSx,
    bgcolor: 'rgba(214, 55, 49, 0.05)',
    borderColor: '#D63731',
    color: '#D63731',
    '&:hover': {
        bgcolor: 'rgba(214, 55, 49, 0.08)'
    }
};

const hangupButtonSx = {
    width: 56,
    height: 56,
    bgcolor: '#D63731',
    color: '#FFFFFF',
    '&:hover': {
        bgcolor: '#BF2E29'
    },
    '&.Mui-disabled': {
        bgcolor: '#F2B9B7',
        color: '#FFFFFF'
    }
};

const getStatusLabel = (status) => {
    switch (status) {
    case 'in-call':
        return 'Em chamada';
    case 'outgoing-ringing':
        return 'A chamar';
    case 'incoming-ringing':
        return 'Chamada recebida';
    case 'negotiating':
        return 'A ligar';
    case 'idle':
        return 'Sem ligação ativa';
    default:
        return 'A preparar chamada';
    }
};

const SIDEBAR_TABS = [
    { key: 'personal', label: 'Dados Pessoais' },
    { key: 'monitoring', label: 'Monitorização' },
    { key: 'assessments', label: 'Notas' },
    { key: 'documents', label: 'Documentos' }
];

const sidebarTabTooltipMap = {
    personal: 'Ver dados pessoais do cuidador e paciente',
    monitoring: 'Ver registos de monitorização',
    assessments: 'Ver e criar notas da chamada',
    documents: 'Ver documentos do cuidador'
};

const sidebarTabSx = {
    width: '100%',
    minHeight: 42,
    px: 1.6,
    borderRadius: '12px',
    borderColor: '#D9D9D9',
    borderWidth: '1.5px',
    textTransform: 'none',
    fontWeight: 700,
    color: '#3D3D3D',
    bgcolor: '#FFFFFF',
    boxShadow: 'none',
    '&:hover': {
        bgcolor: 'rgba(214, 55, 49, 0.05)',
        borderColor: '#D63731',
        borderWidth: '1.5px',
        boxShadow: 'none'
    }
};

const sidebarTabActiveSx = {
    ...sidebarTabSx,
    bgcolor: 'rgba(214, 55, 49, 0.05)',
    color: '#D63731',
    borderColor: '#D63731',
    '&:hover': {
        bgcolor: 'rgba(214, 55, 49, 0.08)',
        borderColor: '#D63731',
        boxShadow: 'none'
    }
};

const sidebarMonitoringChipSx = {
    minHeight: 38,
    borderRadius: '999px',
    fontWeight: 700,
    borderWidth: '2px'
};

const sidebarCardSx = {
    bgcolor: '#FFFFFF',
    borderRadius: '22px',
    boxShadow: 'none',
    border: '1px solid #ECECEC',
    height: '100%',
    minHeight: { xs: '50vh', md: '60vh' },
    maxHeight: { xs: '66vh', md: '74vh' },
    display: 'flex',
    flexDirection: 'column'
};

const sidebarBodySx = {
    pt: 0.5,
    px: 2.2,
    pb: 2.2,
    flex: 1,
    display: 'flex',
    minHeight: 0
};

const videoPanelSx = {
    position: 'relative',
    bgcolor: '#171717',
    color: '#F7F7F7',
    borderRadius: '22px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    height: '100%',
    minHeight: { xs: '44vh', md: '60vh' },
    maxHeight: { xs: '56vh', md: '74vh' },
    border: '1px solid #202020'
};

const compactTableHeaderSx = (columns) => ({
    px: 1.5,
    py: 0.95,
    display: 'grid',
    gridTemplateColumns: columns,
    gap: 1,
    bgcolor: '#3D3D3D',
    color: '#fff',
    borderRadius: '10px',
    boxShadow: 'none'
});

const compactTableRowSx = (columns) => ({
    px: 1.5,
    py: 1.05,
    display: 'grid',
    gridTemplateColumns: columns,
    gap: 1,
    alignItems: 'center',
    bgcolor: '#ECECEC',
    borderRadius: '12px',
    boxShadow: 'none'
});

const monitoringTypes = [
    { key: 'BLOOD_PRESSURE', label: 'Pressão Arterial' },
    { key: 'BLOOD_SUGAR', label: 'Glicemia' },
    { key: 'BODY_TEMPERATURE', label: 'Temperatura' },
    { key: 'HEARTRATE', label: 'Freq. Cardíaca' },
    { key: 'BLOOD_OXYGEN', label: 'Oxigénio' },
    { key: 'WEIGHT', label: 'Peso' },
    { key: 'EXERCISE', label: 'Exercício' }
];

const ONLINE_WINDOW_MS = 120 * 1000;
const PUSH_WAKEUP_POLL_INTERVAL_MS = 2000;
const PUSH_WAKEUP_TIMEOUT_MS = 45000;
const PUSH_ONLINE_GRACE_MS = 3000;
const MOBILE_PUSH_CALL_ROLES = [
    'PATIENT_CARE_GIVER',
    'PATIENT',
    'ACCOUNT_ADMIN',
    'CLINIC_CARE_GIVER'
];

const parseLastPingTs = (value) => {
    if (typeof value !== 'string' || !value.trim()) {
        return null;
    }

    const ts = Date.parse(value);
    return Number.isNaN(ts) ? null : ts;
};

const isRoleOnlineFromPing = (value) => {
    const ts = parseLastPingTs(value);
    if (!ts) {
        return false;
    }

    return Date.now() - ts <= ONLINE_WINDOW_MS;
};

const Call = () => {
    const { id: calleeUserId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { user } = useUser();
    const isLocalDevMode = callConfig.mode === 'local-dev';
    const linkedAppointmentUuid = location.state?.appointmentUuid || null;

    const [mediaError, setMediaError] = useState(null);
    const [mediaBootstrapReady, setMediaBootstrapReady] = useState(false);
    const [showOutgoingPreparingDialog, setShowOutgoingPreparingDialog] = useState(false);
    const [outgoingWakeupStage, setOutgoingWakeupStage] = useState('');
    const [outgoingWakeupError, setOutgoingWakeupError] = useState('');
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [selectedAudio, setSelectedAudio] = useState('');
    const [selectedVideo, setSelectedVideo] = useState('');
    const [notes, setNotes] = useState('');
    const [notesSaving, setNotesSaving] = useState(false);
    const [endingCallAfterSave, setEndingCallAfterSave] = useState(false);
    const [notesError, setNotesError] = useState('');
    const [notesSuccess, setNotesSuccess] = useState('');
    const [notesPriviledged, setNotesPriviledged] = useState(false);
    const [elapsedSeconds, setElapsedSeconds] = useState(0);
    const [outgoingRingBlocked, setOutgoingRingBlocked] = useState(false);
    const [remoteUser, setRemoteUser] = useState(location.state?.user || null);
    const [sidebarTab, setSidebarTab] = useState('personal');
    const [sidebarPatient, setSidebarPatient] = useState(null);
    const [sidebarLoadingPatient, setSidebarLoadingPatient] = useState(false);
    const [sidebarMeasurements, setSidebarMeasurements] = useState([]);
    const [sidebarLoadingMeasurements, setSidebarLoadingMeasurements] = useState(false);
    const [sidebarMeasurementType, setSidebarMeasurementType] = useState('BLOOD_PRESSURE');
    const [sidebarAssessmentNotes, setSidebarAssessmentNotes] = useState([]);
    const [sidebarLoadingAssessmentNotes, setSidebarLoadingAssessmentNotes] = useState(false);
    const [sidebarDocuments, setSidebarDocuments] = useState([]);
    const [sidebarLoadingDocuments, setSidebarLoadingDocuments] = useState(false);
    const [sidebarSelectedNote, setSidebarSelectedNote] = useState(null);
    const [sidebarNoteDialogOpen, setSidebarNoteDialogOpen] = useState(false);
    const [sidebarLoadingSelectedNote, setSidebarLoadingSelectedNote] = useState(false);
    const [sidebarSelectedNoteError, setSidebarSelectedNoteError] = useState('');

    const localVideoRef = useRef(null);
    const remoteVideoRef = useRef(null);
    const remoteAudioRef = useRef(null);
    const mediaBootstrapRef = useRef(false);
    const autoCreateRef = useRef(false);
    const outgoingCallLogUuidRef = useRef(null);
    const outgoingCallLogStateRef = useRef(null);
    const outgoingTimeoutRef = useRef(null);
    const postRingingFinalizeRef = useRef(null);
    const rtcEndedFallbackTimeoutRef = useRef(null);
    const outgoingCancelledByUserRef = useRef(false);
    const outgoingSignalCallIdRef = useRef(null);
    const callEndedReasonRef = useRef(null);
    const processedWsLogCountRef = useRef(0);
    const wasInCallRef = useRef(false);
    const wasOutgoingRingingRef = useRef(false);
    const isOutgoingRingingRef = useRef(false);
    const isInCallRef = useRef(false);
    const isNegotiatingRef = useRef(false);
    const hasActiveCallRef = useRef(false);
    const currentUiStatusRef = useRef('idle');
    const prevUiStatusRef = useRef(null);
    const callStartedAtRef = useRef(null);
    const callRedirectHandledRef = useRef(false);
    const outgoingWakeupCancelledRef = useRef(false);
    const currentCallIdRef = useRef(null);
    const lastCallEndedFrameRef = useRef(null);
    const savePendingNoteBeforeExitRef = useRef(async () => true);
    const outgoingRingAudioRef = useRef(null);
    const outgoingRingUnlockedRef = useRef(false);
    const activeAppointmentUuidRef = useRef(linkedAppointmentUuid);
    const appointmentMarkedOngoingRef = useRef(false);
    const appointmentMarkedCompletedRef = useRef(false);

    const {
        state,
        wsMessageLog,
        uiState,
        localStream,
        remoteStream,
        errorMessage,
        actions,
        bootstrapError,
        outgoingCallUi,
        outgoingFeedback,
        cancelOutgoingCall,
        dismissOutgoingFeedback
    } = useCallManager();

    const audioDevices = useMemo(
        () => state?.media?.devices?.audioInputs || [],
        [state?.media?.devices?.audioInputs]
    );
    const videoDevices = useMemo(
        () => state?.media?.devices?.videoInputs || [],
        [state?.media?.devices?.videoInputs]
    );
    const speaking = Boolean(state?.media?.isSpeaking && uiState.isLocalAudioEnabled);
    const activeRemoteUserId = state?.activeCall?.remoteUserId || null;
    const shouldAutoDial = Boolean(location.state?.autoDial);
    const autoDialRemoteRoleType = remoteUser?.role || location.state?.user?.role || '';
    const shouldWakeUserIfOfflineForAutoDial = Boolean(location.state?.wakeIfOffline);
    const shouldSendPushBeforeCreateCall =
        shouldAutoDial && MOBILE_PUSH_CALL_ROLES.includes(autoDialRemoteRoleType);
    const isRemoteKnownOnlineForAutoDial =
        typeof location.state?.user?.isOnline === 'boolean'
            ? location.state.user.isOnline
            : isRoleOnlineFromPing(
                  remoteUser?.last_call_ping ||
                      remoteUser?.lastCallPing ||
                      location.state?.user?.last_call_ping ||
                      location.state?.user?.lastCallPing ||
                      ''
              );
    const debugCallLog = (...args) => {
    };

    const waitForUserOnlineAfterPushForAutoDial = useCallback(async () => {
        const startedAt = Date.now();

        while (!outgoingWakeupCancelledRef.current && Date.now() - startedAt < PUSH_WAKEUP_TIMEOUT_MS) {
            try {
                const resp = await fetchUserByRole({
                    userRoleUuid: user.selectedRole,
                    roleUuid: calleeUserId
                });
                const nextRemoteUser = resp?.data || null;

                if (nextRemoteUser) {
                    setRemoteUser(nextRemoteUser);
                }

                if (isRoleOnlineFromPing(nextRemoteUser?.last_call_ping || nextRemoteUser?.lastCallPing || '')) {
                    return true;
                }
            } catch {
                // keep polling inside the timeout window
            }

            await new Promise((resolve) => {
                window.setTimeout(resolve, PUSH_WAKEUP_POLL_INTERVAL_MS);
            });
        }

        return false;
    }, [calleeUserId, user.selectedRole]);

    const waitForOnlinePushDeliveryWindow = useCallback(async () => {
        const startedAt = Date.now();

        while (!outgoingWakeupCancelledRef.current && Date.now() - startedAt < PUSH_ONLINE_GRACE_MS) {
            await new Promise((resolve) => {
                window.setTimeout(resolve, 250);
            });
        }

        return !outgoingWakeupCancelledRef.current;
    }, []);

    const releaseLocalMedia = useCallback(async () => {
        try {
            await actions.stopLocalMedia?.();
        } catch {
            // best effort
        }

        if (localVideoRef.current) {
            localVideoRef.current.srcObject = null;
        }
        if (remoteVideoRef.current) {
            remoteVideoRef.current.srcObject = null;
        }
        if (remoteAudioRef.current) {
            remoteAudioRef.current.srcObject = null;
        }
    }, [actions]);

    const clearOutgoingTimeout = useCallback(() => {
        if (outgoingTimeoutRef.current) {
            clearTimeout(outgoingTimeoutRef.current);
            outgoingTimeoutRef.current = null;
        }
        if (postRingingFinalizeRef.current) {
            clearTimeout(postRingingFinalizeRef.current);
            postRingingFinalizeRef.current = null;
        }
    }, []);

    const clearRtcEndedFallbackTimeout = useCallback(() => {
        if (rtcEndedFallbackTimeoutRef.current) {
            clearTimeout(rtcEndedFallbackTimeoutRef.current);
            rtcEndedFallbackTimeoutRef.current = null;
        }
    }, []);

    const resolveFinalRingingState = useCallback(() => {
        if (outgoingCancelledByUserRef.current) {
            return 'REJECTED';
        }

        const reason = (callEndedReasonRef.current || '').toUpperCase();
        if (reason === 'REJECTED') {
            return 'REJECTED';
        }
        if (reason === 'RING_TIMEOUT' || reason === 'TIMEOUT' || reason === 'NO_ANSWER') {
            return 'IGNORED';
        }

        return 'IGNORED';
    }, []);

    const patchOutgoingLogState = useCallback(async (stateTxt) => {
        const callLogUuid = outgoingCallLogUuidRef.current;
        if (!callLogUuid || !user.selectedRole) {
            debugCallLog('skip patch', {
                reason: 'missing callLogUuid or selectedRole',
                callLogUuid,
                selectedRole: user.selectedRole
            });
            return false;
        }

        if (outgoingCallLogStateRef.current === stateTxt) {
            debugCallLog('skip patch', {
                reason: 'same state',
                stateTxt,
                current: outgoingCallLogStateRef.current
            });
            return false;
        }

        const payload = {
            call: callLogUuid,
            state: stateTxt
        };

        if (stateTxt === 'ONGOING') {
            payload.ti = new Date().toISOString();
        }

        if (stateTxt === 'ENDED') {
            payload.tf = new Date().toISOString();
        }

        try {
            debugCallLog('patch start', { payload });
            await updateCallLog({
                userRoleUuid: user.selectedRole,
                payload
            });
            outgoingCallLogStateRef.current = stateTxt;
            debugCallLog('patch success', { callLogUuid, stateTxt });
        } catch (error) {
            debugCallLog('patch error', {
                callLogUuid,
                stateTxt,
                message: error?.response?.data?.description || error?.message || String(error)
            });
            // Non-blocking: avoid disrupting active call flow.
        }
    }, [user.selectedRole]);

    const patchLinkedAppointmentStatus = useCallback(async (status, timestamp) => {
        const appointmentUuid = activeAppointmentUuidRef.current;

        if (!appointmentUuid || !user.selectedRole) {
            return false;
        }

        if (status === 'ONGOING' && appointmentMarkedOngoingRef.current) {
            return true;
        }

        if (status === 'COMPLETED' && appointmentMarkedCompletedRef.current) {
            return true;
        }

        const payload = {
            appointment: appointmentUuid,
            status
        };

        if (status === 'ONGOING') {
            payload.ti = timestamp || new Date().toISOString();
        }

        if (status === 'COMPLETED') {
            payload.tf = timestamp || new Date().toISOString();
        }

        try {
            await updateAppointmentAdmin({
                userRoleUuid: user.selectedRole,
                payload
            });

            if (status === 'ONGOING') {
                appointmentMarkedOngoingRef.current = true;
            }

            if (status === 'COMPLETED') {
                appointmentMarkedCompletedRef.current = true;
            }

            return true;
        } catch {
            return false;
        }
    }, [user.selectedRole]);

    const finalizeOutgoingRingingLog = useCallback(async (source) => {
        if (!outgoingCallLogUuidRef.current || outgoingCallLogStateRef.current !== 'RINGING') {
            return false;
        }

        const finalState = resolveFinalRingingState();
        debugCallLog('finalize outgoing ringing', {
            source,
            reason: callEndedReasonRef.current,
            cancelledByUser: outgoingCancelledByUserRef.current,
            finalState
        });
            await patchOutgoingLogState(finalState);
            await releaseLocalMedia();
            if (!callRedirectHandledRef.current) {
                callRedirectHandledRef.current = true;
                navigate('/', {
                replace: true,
                state: {
                    callResult: {
                        status: finalState,
                        durationSec: 0
                    }
                }
            });
        }
        clearOutgoingTimeout();
        outgoingCallLogUuidRef.current = null;
        outgoingCallLogStateRef.current = null;
        outgoingSignalCallIdRef.current = null;
        callEndedReasonRef.current = null;
        wasOutgoingRingingRef.current = false;
        outgoingCancelledByUserRef.current = false;
    }, [clearOutgoingTimeout, navigate, patchOutgoingLogState, releaseLocalMedia, resolveFinalRingingState]);

    const scheduleIgnoredTimeout = useCallback(() => {
        clearOutgoingTimeout();

        if (!outgoingCallLogUuidRef.current) {
            debugCallLog('skip scheduleIgnoredTimeout', { reason: 'no callLogUuid yet' });
            return false;
        }

        debugCallLog('schedule ignored timeout', { callLogUuid: outgoingCallLogUuidRef.current });
        outgoingTimeoutRef.current = setTimeout(async () => {
            outgoingTimeoutRef.current = null;

            if (!isOutgoingRingingRef.current || isInCallRef.current) {
                debugCallLog('timeout fired but skipped', {
                    isOutgoingRinging: isOutgoingRingingRef.current,
                    isInCall: isInCallRef.current
                });
                return;
            }

            debugCallLog('timeout fired -> patch IGNORED');
            await patchOutgoingLogState('IGNORED');
            await actions.endCall();
            await releaseLocalMedia();
            if (!callRedirectHandledRef.current) {
                callRedirectHandledRef.current = true;
                navigate('/', {
                    replace: true,
                    state: {
                        callResult: {
                            status: 'IGNORED',
                            durationSec: 0
                        }
                    }
                });
            }

            outgoingCallLogUuidRef.current = null;
            outgoingCallLogStateRef.current = null;
            wasOutgoingRingingRef.current = false;
            outgoingCancelledByUserRef.current = false;
        }, 30000);
    }, [actions, clearOutgoingTimeout, navigate, patchOutgoingLogState, releaseLocalMedia]);

    const ensureOutgoingCallLog = useCallback(async ({ scheduleTimeout = false } = {}) => {
        if (!user.selectedRole || !calleeUserId) {
            return null;
        }

        if (outgoingCallLogUuidRef.current) {
            if (
                scheduleTimeout &&
                outgoingCallLogStateRef.current === 'RINGING' &&
                !outgoingTimeoutRef.current
            ) {
                scheduleIgnoredTimeout();
            }
            return outgoingCallLogUuidRef.current;
        }

        try {
            debugCallLog('createCallLog start', {
                srcRoleUuid: user.selectedRole,
                dstRoleUuid: calleeUserId
            });
            const resp = await createCallLog({
                userRoleUuid: user.selectedRole,
                srcRoleUuid: user.selectedRole,
                dstRoleUuid: calleeUserId
            });
            const callLogUuid = resp?.data?.uuid || null;

            if (callLogUuid) {
                outgoingCallLogUuidRef.current = callLogUuid;
                outgoingCallLogStateRef.current = 'RINGING';
                callEndedReasonRef.current = null;
                debugCallLog('createCallLog success', { callLogUuid });

                if (scheduleTimeout) {
                    scheduleIgnoredTimeout();
                }
            } else {
                debugCallLog('createCallLog missing uuid', { response: resp?.data });
            }

            return callLogUuid;
        } catch (error) {
            debugCallLog('createCallLog error', {
                message: error?.response?.data?.description || error?.message || String(error)
            });
            return null;
        }
    }, [calleeUserId, scheduleIgnoredTimeout, user.selectedRole]);

    const performOutgoingCallCreate = useCallback(async () => {
        const result = await actions.createCall({
            calleeUserId,
            audio: true,
            video: true
        });

        if (!result?.ok) {
            debugCallLog('createCall failed', { result });
            return false;
        }

        debugCallLog('createCall success', { calleeUserId });

        await ensureOutgoingCallLog({ scheduleTimeout: true });

        return true;
    }, [actions, calleeUserId, ensureOutgoingCallLog]);

    useEffect(() => {
        if (!localVideoRef.current) return;
        localVideoRef.current.srcObject = localStream || null;
    }, [localStream, uiState.isLocalVideoEnabled]);

    useEffect(() => {
        if (remoteVideoRef.current) {
            remoteVideoRef.current.srcObject = remoteStream || null;
            if (remoteStream) {
                void remoteVideoRef.current.play().catch(() => {
                    // best effort; autoplay can be blocked by browser policy
                });
            }
        }

        if (remoteAudioRef.current) {
            remoteAudioRef.current.srcObject = remoteStream || null;
            if (remoteStream) {
                void remoteAudioRef.current.play().catch(() => {
                    // best effort; autoplay can be blocked by browser policy
                });
            }
        }
    }, [remoteStream, uiState.hasRemoteMedia, uiState.isInCall, uiState.isNegotiating]);

    useEffect(() => {
        if (
            !callConfig.featureEnabled ||
            (!isLocalDevMode && !user.selectedRole) ||
            mediaBootstrapRef.current
        ) {
            return;
        }

        mediaBootstrapRef.current = true;

        const bootstrap = async () => {
            setMediaError(null);
            setMediaBootstrapReady(false);

            const mediaResult = await actions.acquireLocalMedia({
                audio: true,
                video: true
            });
            if (!mediaResult.ok) {
                const fallbackAudioOnlyResult = await actions.acquireLocalMedia({
                    audio: true,
                    video: false
                });

                if (!fallbackAudioOnlyResult.ok) {
                    setMediaError(mediaResult.error);
                    return;
                }
            }

            await actions.refreshDevices();
            setMediaBootstrapReady(true);
        };

        void bootstrap();
    }, [actions, isLocalDevMode, user.selectedRole]);

    useEffect(() => {
        if (!audioDevices.length || selectedAudio) return;
        setSelectedAudio(audioDevices[0].deviceId);
    }, [audioDevices, selectedAudio]);

    useEffect(() => {
        if (!shouldAutoDial || !calleeUserId || autoCreateRef.current) {
            setShowOutgoingPreparingDialog(false);
            return;
        }

        if (uiState.isOutgoingRinging || uiState.isInCall || activeRemoteUserId === calleeUserId) {
            setShowOutgoingPreparingDialog(false);
            return;
        }

        if (!mediaBootstrapReady) {
            setShowOutgoingPreparingDialog(true);
            return;
        }

        setShowOutgoingPreparingDialog(false);
    }, [
        activeRemoteUserId,
        calleeUserId,
        mediaBootstrapReady,
        shouldAutoDial,
        uiState.isInCall,
        uiState.isOutgoingRinging
    ]);

    useEffect(() => {
        if (!videoDevices.length || selectedVideo) return;
        setSelectedVideo(videoDevices[0].deviceId);
    }, [selectedVideo, videoDevices]);

    useEffect(() => {
        if (!callConfig.featureEnabled) return;
        if (!calleeUserId || autoCreateRef.current) return;
        if (!shouldAutoDial) return;
        if (!uiState.canCreateCall) return;
        if (!mediaBootstrapReady) return;
        if (activeRemoteUserId && activeRemoteUserId !== calleeUserId) return;

        autoCreateRef.current = true;
        const startOutgoingCall = async () => {
            outgoingWakeupCancelledRef.current = false;
            setOutgoingWakeupError('');

            let shouldCreateDirectly =
                !shouldWakeUserIfOfflineForAutoDial || isRemoteKnownOnlineForAutoDial;

            if (!shouldCreateDirectly && user.selectedRole) {
                try {
                    const resp = await fetchUserByRole({
                        userRoleUuid: user.selectedRole,
                        roleUuid: calleeUserId
                    });
                    const nextRemoteUser = resp?.data || null;
                    if (nextRemoteUser) {
                        setRemoteUser(nextRemoteUser);
                    }
                    shouldCreateDirectly = isRoleOnlineFromPing(
                        nextRemoteUser?.last_call_ping || nextRemoteUser?.lastCallPing || ''
                    );
                } catch {
                    shouldCreateDirectly = false;
                }
            }

            if (!shouldCreateDirectly && shouldWakeUserIfOfflineForAutoDial && user.selectedRole) {
                try {
                    await ensureOutgoingCallLog();
                    setOutgoingWakeupStage('sending-push');
                    await sendCallPush({
                        userRoleUuid: user.selectedRole,
                        dstRoleUuid: calleeUserId
                    });
                    if (outgoingWakeupCancelledRef.current) {
                        return;
                    }

                    setOutgoingWakeupStage('waiting-online');
                    const isNowOnline = await waitForUserOnlineAfterPushForAutoDial();
                    if (outgoingWakeupCancelledRef.current) {
                        return;
                    }

                    if (!isNowOnline) {
                        setOutgoingWakeupStage('');
                        setOutgoingWakeupError('O utilizador não ficou disponível após a notificação push.');
                        await patchOutgoingLogState('IGNORED');
                        await releaseLocalMedia();
                        if (!callRedirectHandledRef.current) {
                            callRedirectHandledRef.current = true;
                            navigate('/', {
                                replace: true,
                                state: {
                                    callResult: {
                                        status: 'IGNORED',
                                        durationSec: 0
                                    }
                                }
                            });
                        }
                        return;
                    }
                } catch (error) {
                    setOutgoingWakeupStage('');
                    setOutgoingWakeupError(
                        error?.response?.data?.description ||
                            'Não foi possível enviar a notificação push para o utilizador.'
                    );
                    return;
                }
            }

            if (shouldCreateDirectly && shouldSendPushBeforeCreateCall && user.selectedRole) {
                try {
                    await ensureOutgoingCallLog();
                    setOutgoingWakeupStage('sending-push');
                    await sendCallPush({
                        userRoleUuid: user.selectedRole,
                        dstRoleUuid: calleeUserId
                    });
                    if (outgoingWakeupCancelledRef.current) {
                        return;
                    }

                    setOutgoingWakeupStage('waiting-push-receipt');
                    const waitCompleted = await waitForOnlinePushDeliveryWindow();
                    if (!waitCompleted || outgoingWakeupCancelledRef.current) {
                        return;
                    }
                } catch (error) {
                    setOutgoingWakeupStage('');
                    setOutgoingWakeupError(
                        error?.response?.data?.description ||
                            'Nao foi possivel enviar a notificacao push para o utilizador.'
                    );
                    return;
                }
            }

            setOutgoingWakeupStage('');
            await performOutgoingCallCreate();
        };

        void startOutgoingCall();
    }, [
        activeRemoteUserId,
        calleeUserId,
        ensureOutgoingCallLog,
        isRemoteKnownOnlineForAutoDial,
        mediaBootstrapReady,
        navigate,
        patchOutgoingLogState,
        performOutgoingCallCreate,
        releaseLocalMedia,
        shouldSendPushBeforeCreateCall,
        shouldAutoDial,
        shouldWakeUserIfOfflineForAutoDial,
        uiState.canCreateCall,
        user.selectedRole,
        waitForOnlinePushDeliveryWindow,
        waitForUserOnlineAfterPushForAutoDial
    ]);

    useEffect(
        () => () => {
            outgoingWakeupCancelledRef.current = true;
            clearOutgoingTimeout();
            clearRtcEndedFallbackTimeout();
        },
        [clearOutgoingTimeout, clearRtcEndedFallbackTimeout]
    );

    useEffect(() => {
        if (!wsMessageLog?.length) {
            processedWsLogCountRef.current = 0;
            return;
        }

        const startIndex = Math.max(processedWsLogCountRef.current, 0);
        const newEntries = wsMessageLog.slice(startIndex);
        processedWsLogCountRef.current = wsMessageLog.length;

        for (const entry of newEntries) {
            if (entry?.direction !== 'in' || !entry?.text) continue;
            try {
                const frame = JSON.parse(entry.text);
                if (frame?.type !== 'CALL_ENDED') continue;
                const frameCallId = frame?.callId || null;
                const reason = frame?.payload?.reason || null;
                lastCallEndedFrameRef.current = { callId: frameCallId, reason };
                if (frameCallId && frameCallId === outgoingSignalCallIdRef.current) {
                    callEndedReasonRef.current = reason;
                    debugCallLog('received CALL_ENDED for outgoing call', { frameCallId, reason });
                }
            } catch {
                // ignore non-JSON entries
            }
        }
    }, [wsMessageLog]);

    useEffect(() => {
        const isOutgoingRinging = Boolean(uiState.isOutgoingRinging);
        const isInCall = Boolean(uiState.isInCall);
        const isNegotiating = Boolean(uiState.isNegotiating);
        isOutgoingRingingRef.current = isOutgoingRinging;
        isInCallRef.current = isInCall;
        isNegotiatingRef.current = isNegotiating;
        hasActiveCallRef.current = Boolean(state?.activeCall);
        currentUiStatusRef.current = uiState.status;

        if (isOutgoingRinging) {
            wasOutgoingRingingRef.current = true;
            if (state?.activeCall?.callId) {
                outgoingSignalCallIdRef.current = state.activeCall.callId;
                currentCallIdRef.current = state.activeCall.callId;
            }
            debugCallLog('ui state', { status: uiState.status, isOutgoingRinging, isInCall });
            if (postRingingFinalizeRef.current) {
                clearTimeout(postRingingFinalizeRef.current);
                postRingingFinalizeRef.current = null;
            }
        } else if (wasOutgoingRingingRef.current && outgoingCallLogStateRef.current === 'RINGING') {
            // Signaling can leave "outgoing-ringing" before reaching "idle".
            // Give it a short settle window and only finalize if the call really
            // ended up back in idle without any active call context. Slow mobile
            // devices can legitimately spend a few seconds in NEGOTIATING.
            postRingingFinalizeRef.current = setTimeout(async () => {
                postRingingFinalizeRef.current = null;
                if (
                    isInCallRef.current ||
                    isNegotiatingRef.current ||
                    hasActiveCallRef.current ||
                    currentUiStatusRef.current !== 'idle'
                ) {
                    return;
                }
                if (!outgoingCallLogUuidRef.current || outgoingCallLogStateRef.current !== 'RINGING') {
                    return;
                }
                await finalizeOutgoingRingingLog('post-ringing-settle');
            }, 2000);
        }

        if (!wasInCallRef.current && isInCall) {
            debugCallLog('transition -> ONGOING');
            if (!callStartedAtRef.current) {
                callStartedAtRef.current = Date.now();
            }
            if (state?.activeCall?.callId) {
                currentCallIdRef.current = state.activeCall.callId;
            }
            void patchLinkedAppointmentStatus('ONGOING');
            void patchOutgoingLogState('ONGOING');
        }

        if (wasInCallRef.current && !isInCall) {
            const lastEnded = lastCallEndedFrameRef.current;
            const callId = currentCallIdRef.current;
            const hasExplicitEnded =
                Boolean(lastEnded?.callId) &&
                Boolean(callId) &&
                lastEnded.callId === callId;

            if (!hasExplicitEnded) {
                debugCallLog('skip ENDED finalize without explicit CALL_ENDED', {
                    callId,
                    lastEndedCallId: lastEnded?.callId || null,
                    uiStatus: uiState.status
                });
            } else {
                debugCallLog('transition -> ENDED (explicit CALL_ENDED)');
                const finalizeEnded = async () => {
                    const saved = await savePendingNoteBeforeExitRef.current();
                    if (!saved) {
                        return;
                    }
                    await patchLinkedAppointmentStatus('COMPLETED');
                    await patchOutgoingLogState('ENDED');
                    await releaseLocalMedia();
                    if (!callRedirectHandledRef.current) {
                        const startedAt = callStartedAtRef.current;
                        const durationSec = startedAt
                            ? Math.max(0, Math.round((Date.now() - startedAt) / 1000))
                            : 0;
                        callRedirectHandledRef.current = true;
                        navigate('/', {
                            replace: true,
                            state: {
                                callResult: {
                                    status: 'ENDED',
                                    durationSec
                                }
                            }
                        });
                    }
                    outgoingCallLogUuidRef.current = null;
                    outgoingCallLogStateRef.current = null;
                    outgoingSignalCallIdRef.current = null;
                    callEndedReasonRef.current = null;
                    wasOutgoingRingingRef.current = false;
                    outgoingCancelledByUserRef.current = false;
                    callStartedAtRef.current = null;
                    currentCallIdRef.current = null;
                    lastCallEndedFrameRef.current = null;
                };
                void finalizeEnded();
            }
        }

        const isIdleWithoutActiveCall =
            uiState.status === 'idle' && (state?.activeCall == null);

        if (
            isIdleWithoutActiveCall &&
            outgoingCallLogUuidRef.current &&
            outgoingCallLogStateRef.current === 'RINGING'
        ) {
            debugCallLog('idle without active call while RINGING log', {
                cancelledByUser: outgoingCancelledByUserRef.current
            });
            void finalizeOutgoingRingingLog('idle-without-active-call');
        }

        wasInCallRef.current = isInCall;
        prevUiStatusRef.current = uiState.status;
    }, [
        actions,
        finalizeOutgoingRingingLog,
        patchLinkedAppointmentStatus,
        releaseLocalMedia,
        state?.activeCall,
        uiState.isInCall,
        uiState.isNegotiating,
        uiState.isOutgoingRinging,
        uiState.status,
        user.selectedRole,
        navigate,
        patchOutgoingLogState
    ]);

    useEffect(() => {
        const prevStatus = prevUiStatusRef.current;
        const currStatus = uiState.status;

        if (
            prevStatus === 'outgoing-ringing' &&
            currStatus === 'idle' &&
            outgoingCallLogUuidRef.current &&
            outgoingCallLogStateRef.current === 'RINGING'
        ) {
            debugCallLog('status transition outgoing-ringing -> idle', {
                cancelledByUser: outgoingCancelledByUserRef.current
            });
            void finalizeOutgoingRingingLog('status-transition-outgoing-ringing-idle');
        }

        prevUiStatusRef.current = currStatus;
    }, [finalizeOutgoingRingingLog, uiState.status, user.selectedRole]);

    useEffect(() => {
        const rtcState = uiState.rtcConnectionState;
        const activeCallId = currentCallIdRef.current || state?.activeCall?.callId || null;
        const lastEnded = lastCallEndedFrameRef.current;
        const hasExplicitEnded =
            Boolean(lastEnded?.callId) &&
            Boolean(activeCallId) &&
            lastEnded.callId === activeCallId;
        const shouldWatchRtcFallback =
            (uiState.isInCall || uiState.isNegotiating) &&
            !callRedirectHandledRef.current &&
            !hasExplicitEnded &&
            (rtcState === 'failed' || rtcState === 'closed');

        if (!shouldWatchRtcFallback) {
            clearRtcEndedFallbackTimeout();
            return;
        }

        if (rtcEndedFallbackTimeoutRef.current) {
            return;
        }

        rtcEndedFallbackTimeoutRef.current = setTimeout(async () => {
            rtcEndedFallbackTimeoutRef.current = null;

            const currentRtcState = uiState.rtcConnectionState;
            const currentActiveCallId = currentCallIdRef.current || state?.activeCall?.callId || null;
            const currentLastEnded = lastCallEndedFrameRef.current;
            const endedBySignaling =
                Boolean(currentLastEnded?.callId) &&
                Boolean(currentActiveCallId) &&
                currentLastEnded.callId === currentActiveCallId;

            if (callRedirectHandledRef.current || endedBySignaling) {
                return;
            }

            if (currentRtcState !== 'failed' && currentRtcState !== 'closed') {
                return;
            }

            const saved = await savePendingNoteBeforeExitRef.current();
            if (!saved) {
                return;
            }

            await patchLinkedAppointmentStatus('COMPLETED');
            await patchOutgoingLogState('ENDED');
            await actions.endCall();
            await releaseLocalMedia();

            if (!callRedirectHandledRef.current) {
                const startedAt = callStartedAtRef.current;
                const durationSec = startedAt
                    ? Math.max(0, Math.round((Date.now() - startedAt) / 1000))
                    : 0;
                callRedirectHandledRef.current = true;
        navigate('/', {
            replace: true,
            state: {
                callResult: {
                            status: 'ENDED',
                            durationSec
                        }
                    }
                });
            }

            outgoingCallLogUuidRef.current = null;
            outgoingCallLogStateRef.current = null;
            outgoingSignalCallIdRef.current = null;
            callEndedReasonRef.current = null;
            wasOutgoingRingingRef.current = false;
            outgoingCancelledByUserRef.current = false;
            callStartedAtRef.current = null;
            currentCallIdRef.current = null;
        }, 5000);
    }, [
        actions,
        clearRtcEndedFallbackTimeout,
        navigate,
        patchLinkedAppointmentStatus,
        patchOutgoingLogState,
        releaseLocalMedia,
        state?.activeCall?.callId,
        uiState.isInCall,
        uiState.isNegotiating,
        uiState.rtcConnectionState
    ]);

    useEffect(() => {
        if (!callConfig.featureEnabled) return;
        if (!calleeUserId) return;

        const isSignalingReadyForCall =
            uiState.signalingConnectionState === 'AUTHENTICATED' ||
            uiState.signalingConnectionState === 'READY';

        if (isSignalingReadyForCall) {
            return;
        }

        void actions.connectSignaling();
    }, [actions, calleeUserId, uiState.signalingConnectionState]);

    const handleSaveDevices = async () => {
        setSettingsOpen(false);

        if (selectedAudio) {
            await actions.selectAudioInputDevice(selectedAudio);
        }

        if (selectedVideo) {
            await actions.selectVideoInputDevice(selectedVideo);
        }
    };

    const handleDownloadDocument = async (documentUuid, fileName) => {
        if (!user?.selectedRole || !documentUuid) {
            return;
        }

        try {
            const response = await downloadDocument({
                userRoleUuid: user.selectedRole,
                documentUuid
            });
            const blob = new Blob([response.data]);
            const objectUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = objectUrl;
            link.download = fileName || 'documento';
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(objectUrl);
        } catch {
            // keep non-blocking in call flow
        }
    };

    const remoteDisplayName = useMemo(() => {
        return (
            remoteUser?.name ||
            remoteUser?.displayName ||
            location.state?.user?.displayName ||
            location.state?.user?.name ||
            'Utilizador'
        );
    }, [location.state?.user?.displayName, location.state?.user?.name, remoteUser?.displayName, remoteUser?.name]);
    const elapsedClock = useMemo(() => {
        const total = Math.max(0, Number(elapsedSeconds) || 0);
        const hours = Math.floor(total / 3600);
        const minutes = Math.floor((total % 3600) / 60);
        const seconds = total % 60;
        const pad = (n) => String(n).padStart(2, '0');
        return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
    }, [elapsedSeconds]);
    const callStatusLabel = useMemo(() => getStatusLabel(uiState.status), [uiState.status]);
    const outgoingDialogOpen =
        Boolean(outgoingCallUi?.isRinging) ||
        showOutgoingPreparingDialog ||
        Boolean(outgoingWakeupStage);
    const outgoingDialogTitle = useMemo(() => {
        if (showOutgoingPreparingDialog) {
            return 'A preparar chamada...';
        }
        if (outgoingWakeupStage === 'sending-push') {
            return 'A enviar notificação...';
        }
        if (outgoingWakeupStage === 'waiting-push-receipt') {
            return 'A aguardar rececao da notificação...';
        }
        if (outgoingWakeupStage === 'waiting-online') {
            return 'À espera do utilizador...';
        }
        return 'A chamar...';
    }, [outgoingWakeupStage, showOutgoingPreparingDialog]);
    const outgoingDialogDescription = useMemo(() => {
        if (showOutgoingPreparingDialog) {
            return 'A adquirir acesso à camara e ao microfone antes de iniciar a chamada.';
        }
        if (outgoingWakeupStage === 'sending-push') {
            return 'Estamos a enviar uma notificação push para acordar a aplicação do utilizador.';
        }
        if (outgoingWakeupStage === 'waiting-push-receipt') {
            return 'A aguardar alguns instantes para que a aplicação receba a notificação antes de iniciar a chamada.';
        }
        if (outgoingWakeupStage === 'waiting-online') {
            return 'A aguardar que o utilizador abra a aplicação e fique disponível para receber a chamada.';
        }
        return 'A ligação está a ser iniciada. Aguarde enquanto o destinatário responde.';
    }, [outgoingWakeupStage, showOutgoingPreparingDialog]);

    const selectedRoleType = useMemo(() => {
        const role = (user?.roles || []).find((item) => item.uuid === user?.selectedRole);
        return role?.role || role?.name || '';
    }, [user?.roles, user?.selectedRole]);
    const canUseHangupButton =
        uiState.canEndCall &&
        uiState.isInCall &&
        uiState.rtcConnectionState === 'connected' &&
        !showOutgoingPreparingDialog &&
        !outgoingWakeupStage &&
        !uiState.isLocalMediaAcquiring;
    const activeRole = useMemo(
        () => (user?.roles || []).find((item) => item.uuid === user?.selectedRole) || null,
        [user?.roles, user?.selectedRole]
    );
    const canCreatePrivateNotes = activeRole?.clinician_type === 'PSYCHOLOGY';
    const canViewNote = useCallback(
        (note) => !note?.priviledged || note?.author?.uuid === user?.selectedRole,
        [user?.selectedRole]
    );
    const testCaregiverRoleUuid = process.env.REACT_APP_CALL_TEST_CAREGIVER_ROLE_UUID || '';
    const remoteRoleType = useMemo(
        () => remoteUser?.role || location.state?.user?.role || '',
        [location.state?.user?.role, remoteUser?.role]
    );
    const isCaregiverRemote = remoteRoleType === 'PATIENT_CARE_GIVER';
    const sidebarCaregiverRoleUuid = useMemo(() => {
        if (isCaregiverRemote) {
            return calleeUserId || '';
        }

        if (selectedRoleType === 'PATIENT_CARE_GIVER') {
            return user?.selectedRole || '';
        }

        return testCaregiverRoleUuid;
    }, [calleeUserId, isCaregiverRemote, selectedRoleType, testCaregiverRoleUuid, user?.selectedRole]);
    const showCaregiverSidebar = Boolean(sidebarCaregiverRoleUuid);
    const sidebarMeasurementSubjectUuid = sidebarPatient?.uuid || '';
    const [sidebarCaregiverUser, setSidebarCaregiverUser] = useState(null);
    const [sidebarLoadingCaregiver, setSidebarLoadingCaregiver] = useState(false);
    const noteSubjectUuid = sidebarCaregiverRoleUuid;
    const isPeerTechnicalCallLayout = !showCaregiverSidebar;

    useEffect(() => {
        const loadRemoteUser = async () => {
            if (!user?.selectedRole || !calleeUserId) {
                return;
            }

            if (
                remoteUser?.userId === calleeUserId ||
                remoteUser?.roleUuid === calleeUserId ||
                remoteUser?.role_uuid === calleeUserId ||
                remoteUser?.uuid === calleeUserId
            ) {
                return;
            }

            try {
                const resp = await fetchUserByRole({
                    userRoleUuid: user.selectedRole,
                    roleUuid: calleeUserId
                });
                setRemoteUser(resp?.data || null);
            } catch {
                setRemoteUser((prev) => prev || null);
            }
        };

        void loadRemoteUser();
    }, [
        calleeUserId,
        remoteUser?.roleUuid,
        remoteUser?.role_uuid,
        remoteUser?.userId,
        remoteUser?.uuid,
        user?.selectedRole
    ]);

    useEffect(() => {
        if (!showCaregiverSidebar || !user?.selectedRole || !sidebarCaregiverRoleUuid) {
            setSidebarCaregiverUser(null);
            return;
        }

        const loadSidebarCaregiver = async () => {
            if (
                isCaregiverRemote &&
                (
                    remoteUser?.roleUuid === sidebarCaregiverRoleUuid ||
                    remoteUser?.role_uuid === sidebarCaregiverRoleUuid ||
                    remoteUser?.uuid === sidebarCaregiverRoleUuid ||
                    remoteUser?.userId === sidebarCaregiverRoleUuid
                )
            ) {
                setSidebarCaregiverUser(remoteUser);
            }

            setSidebarLoadingCaregiver(true);
            try {
                const resp = await fetchUserByRole({
                    userRoleUuid: user.selectedRole,
                    roleUuid: sidebarCaregiverRoleUuid
                });
                setSidebarCaregiverUser(resp?.data || null);
            } catch {
                setSidebarCaregiverUser(null);
            } finally {
                setSidebarLoadingCaregiver(false);
            }
        };

        void loadSidebarCaregiver();
    }, [
        isCaregiverRemote,
        remoteUser,
        showCaregiverSidebar,
        sidebarCaregiverRoleUuid,
        user?.selectedRole
    ]);

    useEffect(() => {
        if (!showCaregiverSidebar || !user?.selectedRole || !sidebarCaregiverRoleUuid) {
            setSidebarPatient(null);
            return;
        }

        const loadPatient = async () => {
            setSidebarLoadingPatient(true);
            try {
                const resp = await listPatients({
                    userRoleUuid: user.selectedRole,
                    caregiverRoleUuid: sidebarCaregiverRoleUuid
                });
                const list = Array.isArray(resp?.data) ? resp.data : [];
                setSidebarPatient(list[0] || null);
            } catch {
                setSidebarPatient(null);
            } finally {
                setSidebarLoadingPatient(false);
            }
        };

        void loadPatient();
    }, [showCaregiverSidebar, sidebarCaregiverRoleUuid, user?.selectedRole]);

    useEffect(() => {
        if (
            !showCaregiverSidebar ||
            sidebarTab !== 'monitoring' ||
            !user?.selectedRole ||
            !sidebarMeasurementSubjectUuid
        ) {
            return;
        }

        const loadMeasurements = async () => {
            setSidebarLoadingMeasurements(true);
            try {
                const resp = await listMeasurements({
                    userRoleUuid: user.selectedRole,
                    subjectRoleUuid: sidebarMeasurementSubjectUuid,
                    measurementTypes: [sidebarMeasurementType],
                    order: 'DESC',
                    paginationLimit: 4
                });
                setSidebarMeasurements(Array.isArray(resp?.data) ? resp.data : []);
            } catch {
                setSidebarMeasurements([]);
            } finally {
                setSidebarLoadingMeasurements(false);
            }
        };

        void loadMeasurements();
    }, [showCaregiverSidebar, sidebarMeasurementSubjectUuid, sidebarMeasurementType, sidebarTab, user?.selectedRole]);

    useEffect(() => {
        if (
            !showCaregiverSidebar ||
            sidebarTab !== 'assessments' ||
            !user?.selectedRole ||
            !noteSubjectUuid
        ) {
            return;
        }

        const loadAssessmentNotes = async () => {
            setSidebarLoadingAssessmentNotes(true);
            try {
                const resp = await listClinicalNotes({
                    userRoleUuid: user.selectedRole,
                    subjectRoleUuid: noteSubjectUuid,
                    order: 'DESC',
                    paginationLimit: 4
                });
                setSidebarAssessmentNotes((Array.isArray(resp?.data) ? resp.data : []).filter(canViewNote));
            } catch {
                setSidebarAssessmentNotes([]);
            } finally {
                setSidebarLoadingAssessmentNotes(false);
            }
        };

        void loadAssessmentNotes();
    }, [canViewNote, showCaregiverSidebar, noteSubjectUuid, sidebarTab, user?.selectedRole, notesSuccess]);

    useEffect(() => {
        if (
            !showCaregiverSidebar ||
            sidebarTab !== 'documents' ||
            !user?.selectedRole ||
            !sidebarCaregiverRoleUuid
        ) {
            return;
        }

        const loadDocuments = async () => {
            setSidebarLoadingDocuments(true);
            try {
                const resp = await listDocuments({
                    userRoleUuid: user.selectedRole,
                    caregiverRoleUuid: sidebarCaregiverRoleUuid,
                    paginationLimit: 5
                });
                setSidebarDocuments(Array.isArray(resp?.data) ? resp.data : []);
            } catch {
                setSidebarDocuments([]);
            } finally {
                setSidebarLoadingDocuments(false);
            }
        };

        void loadDocuments();
    }, [showCaregiverSidebar, sidebarCaregiverRoleUuid, sidebarTab, user?.selectedRole]);

    const formatDateTime = useCallback((value) => {
        if (!value) return '--';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return value;
        return date.toLocaleString('pt-PT');
    }, []);

    const formatDateOnly = useCallback((value) => {
        if (!value) return '--';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return value;
        return date.toLocaleDateString('pt-PT');
    }, []);

    const withUnit = useCallback((value, unit) => {
        if (value == null || value === '') return '--';
        return `${value} ${unit}`;
    }, []);

    const caregiverSidebarSummary = useMemo(() => {
        if (!sidebarCaregiverUser) {
            return null;
        }

        const extra = sidebarCaregiverUser?.user_data || {};
        const address = sidebarCaregiverUser?.address || {};

        return {
            name: sidebarCaregiverUser?.name || '--',
            email: sidebarCaregiverUser?.email || '--',
            phone: sidebarCaregiverUser?.phone || '--',
            birthDate: formatDateOnly(sidebarCaregiverUser?.birth_date),
            street: address?.street || '--',
            zip: address?.zip || '--',
            parish: extra?.parish || '--',
            municipality: extra?.municipality || '--',
            professionalStatus: extra?.professional_status || '--'
        };
    }, [formatDateOnly, sidebarCaregiverUser]);

    const mapMonitoringRow = useCallback((measurement) => {
        const data = measurement?.data || {};
        switch (sidebarMeasurementType) {
        case 'BLOOD_PRESSURE':
            return {
                id: measurement?.uuid || Math.random().toString(36),
                date: formatDateTime(measurement?.ts),
                primary: withUnit(data?.systolic, 'mmHg'),
                secondary: withUnit(data?.diastolic, 'mmHg'),
                tertiary: withUnit(data?.hr, 'bpm')
            };
        case 'BLOOD_SUGAR':
            return {
                id: measurement?.uuid || Math.random().toString(36),
                date: formatDateTime(measurement?.ts),
                primary: withUnit(data?.glucose, 'mg/dL'),
                secondary: '--',
                tertiary: '--'
            };
        case 'BODY_TEMPERATURE':
            return {
                id: measurement?.uuid || Math.random().toString(36),
                date: formatDateTime(measurement?.ts),
                primary: withUnit(data?.temperature, 'ºC'),
                secondary: '--',
                tertiary: '--'
            };
        case 'HEARTRATE':
            return {
                id: measurement?.uuid || Math.random().toString(36),
                date: formatDateTime(measurement?.ts),
                primary: withUnit(data?.hr, 'bpm'),
                secondary: '--',
                tertiary: '--'
            };
        case 'BLOOD_OXYGEN':
            return {
                id: measurement?.uuid || Math.random().toString(36),
                date: formatDateTime(measurement?.ts),
                primary: withUnit(data?.saturation, '%'),
                secondary: withUnit(data?.hr, 'bpm'),
                tertiary: '--'
            };
        case 'WEIGHT':
            return {
                id: measurement?.uuid || Math.random().toString(36),
                date: formatDateTime(measurement?.ts),
                primary: withUnit(data?.weight, 'kg'),
                secondary: '--',
                tertiary: '--'
            };
        case 'EXERCISE':
            return {
                id: measurement?.uuid || Math.random().toString(36),
                date: formatDateTime(measurement?.ts),
                primary: data?.activity || '--',
                secondary: withUnit(data?.durationMin, 'min'),
                tertiary: '--'
            };
        default:
            return {
                id: measurement?.uuid || Math.random().toString(36),
                date: formatDateTime(measurement?.ts),
                primary: '--',
                secondary: '--',
                tertiary: '--'
            };
        }
    }, [formatDateTime, sidebarMeasurementType, withUnit]);

    const monitoringColumns = useMemo(() => {
        switch (sidebarMeasurementType) {
        case 'BLOOD_PRESSURE':
            return ['Data', 'Sistólica', 'Diastólica', 'FC'];
        case 'BLOOD_SUGAR':
            return ['Data', 'Glicose', '', ''];
        case 'BODY_TEMPERATURE':
            return ['Data', 'Temperatura', '', ''];
        case 'HEARTRATE':
            return ['Data', 'FC', '', ''];
        case 'BLOOD_OXYGEN':
            return ['Data', 'SpO2', 'FC', ''];
        case 'WEIGHT':
            return ['Data', 'Peso', '', ''];
        case 'EXERCISE':
            return ['Data', 'Atividade', 'Duração', ''];
        default:
            return ['Data', 'Valor', '', ''];
        }
    }, [sidebarMeasurementType]);
    const visibleMonitoringColumns = useMemo(() => monitoringColumns.filter(Boolean), [monitoringColumns]);
    const monitoringGridTemplateColumns = useMemo(() => {
        switch (visibleMonitoringColumns.length) {
        case 4:
            return '1.4fr 1fr 1fr 1fr';
        case 3:
            return '1.4fr 1fr 1fr';
        case 2:
            return '1.6fr 1fr';
        default:
            return '1fr';
        }
    }, [visibleMonitoringColumns.length]);

    const handleSaveNote = useCallback(async () => {
        setNotesError('');
        setNotesSuccess('');

        if (!user?.selectedRole) {
            setNotesError('Não foi possível identificar o USER-ROLE ativo.');
            return;
        }

        if (!calleeUserId) {
            setNotesError('Não foi possível identificar o utilizador remoto.');
            return;
        }

        if (!noteSubjectUuid) {
            setNotesError(
                'As notas clí­nicas não estão disponí­veis para chamadas com cuidador ou paciente.'
            );
            return;
        }

        const content = notes.trim();
        if (!content) {
            setNotesError('Escreve conteúdo para a nota.');
            return;
        }

        setNotesSaving(true);
        try {
            const nowIso = new Date().toISOString();

            await createClinicalNote({
                userRoleUuid: user.selectedRole,
                payload: {
                    ts: nowIso,
                    subject: noteSubjectUuid,
                    ...(activeAppointmentUuidRef.current
                        ? { appointment: activeAppointmentUuidRef.current }
                        : {}),
                    format: 'PLAIN',
                    content,
                    ...(canCreatePrivateNotes ? { priviledged: notesPriviledged } : {})
                }
            });

            setNotes('');
            setNotesPriviledged(false);
            setNotesSuccess('Nota guardada com sucesso.');
            return true;
        } catch (error) {
            const message =
                error?.response?.data?.description ||
                error?.message ||
                'Falha ao guardar nota.';
            setNotesError(message);
            return false;
        } finally {
            setNotesSaving(false);
        }
    }, [
        calleeUserId,
        canCreatePrivateNotes,
        noteSubjectUuid,
        notes,
        notesPriviledged,
        user?.selectedRole
    ]);

    const savePendingNoteBeforeExit = useCallback(async () => {
        if (!notes.trim()) {
            return true;
        }

        setEndingCallAfterSave(true);
        const saved = await handleSaveNote();
        setEndingCallAfterSave(false);
        return Boolean(saved);
    }, [handleSaveNote, notes]);

    useEffect(() => {
        savePendingNoteBeforeExitRef.current = savePendingNoteBeforeExit;
    }, [savePendingNoteBeforeExit]);

    const handleOpenSidebarNote = useCallback(
        async (note) => {
            if (!note?.uuid || !user?.selectedRole) {
                return;
            }

            setSidebarSelectedNote(note);
            setSidebarSelectedNoteError('');
            setSidebarLoadingSelectedNote(true);
            setSidebarNoteDialogOpen(true);

            try {
                const resp = await fetchClinicalNote({
                    userRoleUuid: user.selectedRole,
                    noteUuid: note.uuid
                });
                setSidebarSelectedNote(resp?.data || note);
            } catch (err) {
                const description = err?.response?.data?.description || '';
                setSidebarSelectedNoteError(
                    description === 'Method not allowed for external user.'
                        ? 'O conteúdo desta nota foi definido como privado.'
                        : description || 'Não foi possível carregar o conteúdo da nota.'
                );
            } finally {
                setSidebarLoadingSelectedNote(false);
            }
        },
        [user?.selectedRole]
    );

    const combinedError = bootstrapError || mediaError || errorMessage;

    useEffect(() => {
        if (!outgoingFeedback) {
            return;
        }

        const isStillRingingLog =
            outgoingCallLogUuidRef.current &&
            outgoingCallLogStateRef.current === 'RINGING';

        if (!isStillRingingLog) {
            return;
        }

        const message = (outgoingFeedback.message || '').toLowerCase();
        const likelyUnanswered =
            message.includes('timeout') ||
            message.includes('unavailable') ||
            message.includes('before answer');

        if (!likelyUnanswered) {
            return;
        }

        const finalizeMissed = async () => {
            debugCallLog('outgoingFeedback indicates unanswered -> patch IGNORED');
            clearOutgoingTimeout();
            await patchOutgoingLogState('IGNORED');
            await releaseLocalMedia();
            outgoingCallLogUuidRef.current = null;
            outgoingCallLogStateRef.current = null;
            outgoingSignalCallIdRef.current = null;
            callEndedReasonRef.current = null;
            wasOutgoingRingingRef.current = false;
            outgoingCancelledByUserRef.current = false;
            callStartedAtRef.current = null;
            currentCallIdRef.current = null;
            lastCallEndedFrameRef.current = null;
        };

        void finalizeMissed();
    }, [clearOutgoingTimeout, outgoingFeedback, patchOutgoingLogState, releaseLocalMedia, user.selectedRole]);

    const handleCancelOutgoing = async () => {
        debugCallLog('handleCancelOutgoing');
        outgoingCancelledByUserRef.current = true;
        outgoingWakeupCancelledRef.current = true;
        setOutgoingWakeupStage('');
        clearOutgoingTimeout();
        await patchOutgoingLogState('IGNORED');
        await releaseLocalMedia();
        if (!callRedirectHandledRef.current) {
            callRedirectHandledRef.current = true;
            navigate('/', {
                replace: true,
                state: {
                    callResult: {
                        status: 'IGNORED',
                        durationSec: 0
                    }
                }
            });
        }
        outgoingCallLogUuidRef.current = null;
        outgoingCallLogStateRef.current = null;
        wasOutgoingRingingRef.current = false;
        await cancelOutgoingCall();
    };

    const handleEndCall = async () => {
        debugCallLog('handleEndCall', {
            isOutgoingRinging: uiState.isOutgoingRinging,
            isInCall: uiState.isInCall
        });
        const saved = await savePendingNoteBeforeExit();
        if (!saved) {
            return;
        }
        if (uiState.isOutgoingRinging && !uiState.isInCall) {
            outgoingCancelledByUserRef.current = true;
            clearOutgoingTimeout();
            await patchOutgoingLogState('IGNORED');
            if (!callRedirectHandledRef.current) {
                callRedirectHandledRef.current = true;
                navigate('/', {
                    replace: true,
                    state: {
                        callResult: {
                            status: 'IGNORED',
                            durationSec: 0
                        }
                    }
                });
            }
            outgoingCallLogUuidRef.current = null;
            outgoingCallLogStateRef.current = null;
            wasOutgoingRingingRef.current = false;
        }
        await actions.endCall();
        await releaseLocalMedia();
    };

    useEffect(() => {
        const hasAttemptedOrHandledCall =
            wasInCallRef.current ||
            wasOutgoingRingingRef.current ||
            Boolean(callStartedAtRef.current) ||
            Boolean(outgoingCallLogUuidRef.current) ||
            Boolean(currentCallIdRef.current) ||
            Boolean(lastCallEndedFrameRef.current);

        if (!hasAttemptedOrHandledCall) {
            return;
        }

        const isIdleWithoutActiveCall =
            uiState.status === 'idle' &&
            state?.activeCall == null &&
            !uiState.isInCall &&
            !uiState.isOutgoingRinging &&
            !uiState.isNegotiating &&
            !uiState.isIncomingRinging;

        if (!isIdleWithoutActiveCall) {
            return;
        }

        void releaseLocalMedia();
    }, [
        releaseLocalMedia,
        state?.activeCall,
        uiState.isInCall,
        uiState.isIncomingRinging,
        uiState.isNegotiating,
        uiState.isOutgoingRinging,
        uiState.status
    ]);

    useEffect(() => {
        if (!uiState.isInCall) {
            setElapsedSeconds(0);
            return;
        }

        if (!callStartedAtRef.current) {
            callStartedAtRef.current = Date.now();
        }

        const tick = () => {
            const startedAt = callStartedAtRef.current || Date.now();
            setElapsedSeconds(Math.max(0, Math.floor((Date.now() - startedAt) / 1000)));
        };

        tick();
        const intervalId = setInterval(tick, 1000);

        return () => clearInterval(intervalId);
    }, [uiState.isInCall]);

    useEffect(() => {
        if (!outgoingRingAudioRef.current) {
            const audio = new Audio('/assets/sounds/outgoing-ring.mp3');
            audio.loop = true;
            audio.preload = 'auto';
            audio.volume = 0.18;
            outgoingRingAudioRef.current = audio;
        }

        const audio = outgoingRingAudioRef.current;
        const shouldRing = uiState.status === 'outgoing-ringing';

        if (shouldRing) {
            audio.currentTime = 0;
            const playPromise = audio.play();
            if (playPromise?.catch) {
                playPromise
                    .then(() => {
                        outgoingRingUnlockedRef.current = true;
                        setOutgoingRingBlocked(false);
                    })
                    .catch(() => {
                        setOutgoingRingBlocked(true);
                    });
            }
        } else {
            audio.pause();
            audio.currentTime = 0;
            setOutgoingRingBlocked(false);
        }

        return () => {
            audio.pause();
            audio.currentTime = 0;
        };
    }, [uiState.status]);

    useEffect(() => {
        const audio = outgoingRingAudioRef.current;
        if (!audio) {
            return undefined;
        }

        const primeOrRetryRing = () => {
            const shouldRing = uiState.status === 'outgoing-ringing';

            if (!outgoingRingUnlockedRef.current) {
                const previousVolume = audio.volume;
                audio.volume = 0;
                const unlockPromise = audio.play();
                if (unlockPromise?.catch) {
                    unlockPromise
                        .then(() => {
                            audio.pause();
                            audio.currentTime = 0;
                            audio.volume = previousVolume;
                            outgoingRingUnlockedRef.current = true;
                            if (shouldRing) {
                                const retryPromise = audio.play();
                                if (retryPromise?.catch) {
                                    retryPromise
                                        .then(() => setOutgoingRingBlocked(false))
                                        .catch(() => setOutgoingRingBlocked(true));
                                }
                            }
                        })
                        .catch(() => {
                            audio.volume = previousVolume;
                        });
                }
                return;
            }

            if (shouldRing) {
                const retryPromise = audio.play();
                if (retryPromise?.catch) {
                    retryPromise
                        .then(() => setOutgoingRingBlocked(false))
                        .catch(() => setOutgoingRingBlocked(true));
                }
            }
        };

        window.addEventListener('pointerdown', primeOrRetryRing);
        window.addEventListener('keydown', primeOrRetryRing);

        return () => {
            window.removeEventListener('pointerdown', primeOrRetryRing);
            window.removeEventListener('keydown', primeOrRetryRing);
        };
    }, [uiState.status]);

    if (!callConfig.featureEnabled) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="info">Call feature is disabled by configuration.</Alert>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                p: { xs: 1.25, md: 1.5 },
                mt: { xs: -0.5, md: -1 },
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
                bgcolor: '#FFFFFF'
            }}
        >

            {outgoingFeedback ? (
                <Alert
                    severity={outgoingFeedback.severity || 'info'}
                    onClose={dismissOutgoingFeedback}
                    sx={{ mb: 2, borderRadius: '16px' }}
                >
                    {outgoingFeedback.message}
                </Alert>
            ) : null}

            {outgoingWakeupError ? (
                <Alert
                    severity="warning"
                    onClose={() => setOutgoingWakeupError('')}
                    sx={{ mb: 2, borderRadius: '16px' }}
                >
                    {outgoingWakeupError}
                </Alert>
            ) : null}

            {endingCallAfterSave ? (
                <Alert severity="info" sx={{ mb: 2, borderRadius: '16px' }}>
                    A guardar nota antes de terminar a chamada...
                </Alert>
            ) : null}

            {combinedError ? (
                <Alert severity="error" sx={{ mb: 2, borderRadius: '16px' }}>
                    {combinedError}
                </Alert>
            ) : null}
            {outgoingRingBlocked ? (
                <Alert severity="warning" sx={{ mb: 2, borderRadius: '16px' }}>
                    O browser bloqueou o som automático da chamada. Clique na página para ativar o som.
                </Alert>
            ) : null}
            <Dialog
                open={outgoingDialogOpen}
                onClose={handleCancelOutgoing}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: dialogPaperSx }}
            >
                <DialogTitle sx={dialogTitleSx}>{outgoingDialogTitle}</DialogTitle>
                <DialogContent sx={{ ...dialogContentSx, pb: 1.5 }}>
                    <Stack spacing={2.25} alignItems="center" textAlign="center">
                        <Box
                            sx={{
                                width: 78,
                                height: 78,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: 'rgba(214, 55, 49, 0.08)',
                                color: '#D63731',
                                border: '1px solid rgba(214, 55, 49, 0.18)'
                            }}
                        >
                            <CallOutlinedIcon sx={{ fontSize: 34 }} />
                        </Box>
                        <Stack spacing={0.75} alignItems="center">
                            <Typography variant="h6" fontWeight={700} color="#171717">
                                {remoteDisplayName || outgoingCallUi?.targetUserId || 'A ligar'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 320 }}>
                                {outgoingDialogDescription}
                            </Typography>
                        </Stack>
                        {showOutgoingPreparingDialog || outgoingWakeupStage ? (
                            <CircularProgress size={28} sx={{ color: '#D63731' }} />
                        ) : null}
                        {outgoingRingBlocked ? (
                            <Typography variant="body2" color="warning.main">
                                Clique na página para ativar o som da chamada.
                            </Typography>
                        ) : null}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ ...dialogActionsSx, justifyContent: 'center' }}>
                    <Button
                        variant="outlined"
                        color="error"
                        onClick={handleCancelOutgoing}
                        startIcon={<CallEndIcon />}
                        sx={secondaryButtonSx}
                    >
                        Cancelar
                    </Button>
                </DialogActions>
            </Dialog>

            <Paper
                sx={{
                    ...shellCardSx,
                    p: { xs: 1.4, md: 1.8 },
                    display: 'flex'
                }}
            >
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1.6 }}>
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={1.2}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', md: 'center' }}
                        sx={{ px: { xs: 0.2, md: 0.5 }, pb: 0.2 }}
                    >
                        <Box>
                            <Typography variant="h5" fontWeight={700} color="#171717">
                                Videochamada{" "}
                                <Typography
                                    component="span"
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    - {callStatusLabel}
                                </Typography>
                            </Typography>
                        </Box>
                        <Box />
                    </Stack>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: isPeerTechnicalCallLayout
                                ? { xs: '1fr', lg: '1fr 1fr' }
                                : { xs: '1fr', lg: '1.4fr 0.86fr' },
                            gap: 1.6,
                            alignItems: 'stretch',
                            width: '100%'
                        }}
                    >
                        <Box sx={{ minWidth: 0 }}>
                            <Box sx={videoPanelSx}>
                                {uiState.hasRemoteMedia ? (
                                    <>
                                        <video
                                            ref={remoteVideoRef}
                                            autoPlay
                                            playsInline
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        />
                                        <audio ref={remoteAudioRef} autoPlay />
                                    </>
                                ) : (
                                    <Stack spacing={1.4} alignItems="center">
                                        <PersonIcon sx={{ fontSize: 110, color: '#CFCFCF' }} />
                                        <Typography variant="body1" fontWeight={600} color="#E2E2E2">
                                            A aguardar vídeo remoto
                                        </Typography>
                                    </Stack>
                                )}

                                {!isPeerTechnicalCallLayout ? (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            right: 16,
                                            bottom: 16,
                                            width: { xs: 126, sm: 168, md: 196 },
                                            aspectRatio: '16 / 10',
                                            borderRadius: '18px',
                                            overflow: 'hidden',
                                            border: speaking ? '3px solid' : '2px solid',
                                            borderColor: speaking ? '#D63731' : 'rgba(255,255,255,0.75)',
                                            bgcolor: '#2B2B2B',
                                            color: '#F7F7F7',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            zIndex: 2,
                                            boxShadow: '0px 10px 24px rgba(0, 0, 0, 0.28)'
                                        }}
                                    >
                                        {uiState.isLocalVideoEnabled ? (
                                            <video
                                                ref={localVideoRef}
                                                autoPlay
                                                playsInline
                                                muted
                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            />
                                        ) : (
                                            <Typography variant="caption">Câmara desligada</Typography>
                                        )}
                                    </Box>
                                ) : null}
                            </Box>
                        </Box>

                        {showCaregiverSidebar ? (
                            <Card sx={sidebarCardSx}>
                                <CardContent sx={sidebarBodySx}>
                                    <Stack spacing={1.2} sx={{ width: '100%', minHeight: 0, mt: 2}}>
                                        <Box
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, minmax(0, 1fr))' },
                                                gap: 0.9,
                                                width: '100%'
                                            }}
                                        >
                                            {SIDEBAR_TABS.map((tab) => (
                                                <Tooltip key={tab.key} title={sidebarTabTooltipMap[tab.key] || tab.label}>
                                                    <Button
                                                        variant="outlined"
                                                        sx={sidebarTab === tab.key ? sidebarTabActiveSx : sidebarTabSx}
                                                        onClick={() => setSidebarTab(tab.key)}
                                                    >
                                                        {tab.label}
                                                    </Button>
                                                </Tooltip>
                                            ))}
                                        </Box>

                                        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 0.4 }}>
                                            {sidebarTab === 'personal' ? (
                                                <Stack spacing={1.5}>
                                                    <Paper sx={{ p: 1.4, borderRadius: '14px', bgcolor: '#F7F7F7', boxShadow: 'none' }}>
                                                        <Typography variant="body2" fontWeight={700} color="#3D3D3D" mb={1}>
                                                            Cuidador
                                                        </Typography>
                                                        {sidebarLoadingCaregiver ? (
                                                            <Typography variant="body2" color="text.secondary">
                                                                A carregar...
                                                            </Typography>
                                                        ) : (
                                                            <Stack spacing={0.8}>
                                                                <Typography variant="body2"><strong>Nome:</strong> {caregiverSidebarSummary?.name || '--'}</Typography>
                                                                <Typography variant="body2"><strong>Email:</strong> {caregiverSidebarSummary?.email || '--'}</Typography>
                                                                <Typography variant="body2"><strong>Telefone:</strong> {caregiverSidebarSummary?.phone || '--'}</Typography>
                                                                <Typography variant="body2"><strong>Data de nascimento:</strong> {caregiverSidebarSummary?.birthDate || '--'}</Typography>
                                                                <Typography variant="body2"><strong>Morada:</strong> {caregiverSidebarSummary?.street || '--'}</Typography>
                                                                <Typography variant="body2"><strong>Código postal:</strong> {caregiverSidebarSummary?.zip || '--'}</Typography>
                                                                <Typography variant="body2"><strong>Freguesia:</strong> {caregiverSidebarSummary?.parish || '--'}</Typography>
                                                                <Typography variant="body2"><strong>Concelho:</strong> {caregiverSidebarSummary?.municipality || '--'}</Typography>
                                                                <Typography variant="body2"><strong>Situação profissional:</strong> {caregiverSidebarSummary?.professionalStatus || '--'}</Typography>
                                                            </Stack>
                                                        )}
                                                    </Paper>
                                                    <Paper sx={{ p: 1.4, borderRadius: '14px', bgcolor: '#F7F7F7', boxShadow: 'none' }}>
                                                        <Typography variant="body2" fontWeight={700} color="#3D3D3D" mb={1}>
                                                            Pessoa a Cuidados
                                                        </Typography>
                                                        {sidebarLoadingPatient ? (
                                                            <Typography variant="body2" color="text.secondary">
                                                                A carregar...
                                                            </Typography>
                                                        ) : sidebarPatient ? (
                                                            <Stack spacing={0.8}>
                                                                <Typography variant="body2"><strong>Nome:</strong> {sidebarPatient?.name || '--'}</Typography>
                                                                <Typography variant="body2"><strong>Data de nascimento:</strong> {sidebarPatient?.birth_date || '--'}</Typography>
                                                                <Typography variant="body2"><strong>Parentesco:</strong> {sidebarPatient?.user_data?.relationship || '--'}</Typography>
                                                                <Typography variant="body2"><strong>Morada:</strong> {sidebarPatient?.address?.street || sidebarPatient?.user_data?.street || '--'}</Typography>
                                                                <Typography variant="body2"><strong>Incapacidade:</strong> {sidebarPatient?.user_data?.disability || '--'}</Typography>
                                                            </Stack>
                                                        ) : (
                                                            <Typography variant="body2" color="text.secondary">
                                                                Sem pessoa a cuidados associada.
                                                            </Typography>
                                                        )}
                                                    </Paper>
                                                </Stack>
                                            ) : null}

                                            {sidebarTab === 'monitoring' ? (
                                                <Stack spacing={1.2}>
                                                    <Stack direction="row" spacing={0.2} useFlexGap flexWrap="wrap">
                                                        {monitoringTypes.map((type) => (
                                                            <Chip
                                                                key={type.key}
                                                                label={type.label}
                                                                clickable
                                                                variant={sidebarMeasurementType === type.key ? 'filled' : 'outlined'}
                                                                color={sidebarMeasurementType === type.key ? 'error' : 'default'}
                                                                sx={{
                                                                    ...sidebarMonitoringChipSx,
                                                                    ...(sidebarMeasurementType === type.key
                                                                        ? {
                                                                            bgcolor: '#D63731',
                                                                            color: '#FFFFFF',
                                                                            borderColor: '#D63731',
                                                                            '&:hover': {
                                                                                bgcolor: '#BF2E29'
                                                                            }
                                                                        }
                                                                        : {
                                                                            bgcolor: '#FFFFFF',
                                                                            color: '#3D3D3D',
                                                                            borderColor: '#D9D9D9',
                                                                            '&:hover': {
                                                                                bgcolor: '#F7F7F7',
                                                                                borderColor: '#D63731'
                                                                            }
                                                                        })
                                                                }}
                                                                onClick={() => setSidebarMeasurementType(type.key)}
                                                            />
                                                        ))}
                                                    </Stack>

                                                    <Paper sx={compactTableHeaderSx(monitoringGridTemplateColumns)}>
                                                        {visibleMonitoringColumns.map((column, index) => (
                                                            <Typography key={`${column}-${index}`} variant="caption" fontWeight={600}>
                                                                {column || ' '}
                                                            </Typography>
                                                        ))}
                                                    </Paper>

                                                    {sidebarLoadingMeasurements ? (
                                                        <Paper sx={compactTableRowSx(monitoringGridTemplateColumns)}>
                                                            <Typography sx={{ gridColumn: '1 / -1' }} variant="body2" color="text.secondary" textAlign="center">
                                                                A carregar medições...
                                                            </Typography>
                                                        </Paper>
                                                    ) : null}

                                                    {!sidebarLoadingMeasurements && sidebarMeasurements.length === 0 ? (
                                                        <Paper sx={compactTableRowSx(monitoringGridTemplateColumns)}>
                                                            <Typography sx={{ gridColumn: '1 / -1' }} variant="body2" color="text.secondary" textAlign="center">
                                                                Sem registos.
                                                            </Typography>
                                                        </Paper>
                                                    ) : null}

                                                    {!sidebarLoadingMeasurements
                                                        ? sidebarMeasurements.map((measurement) => {
                                                            const row = mapMonitoringRow(measurement);
                                                            const rowValues = [row.date, row.primary, row.secondary, row.tertiary]
                                                                .slice(0, visibleMonitoringColumns.length);
                                                            return (
                                                                <Paper key={row.id} sx={compactTableRowSx(monitoringGridTemplateColumns)}>
                                                                    {rowValues.map((value, index) => (
                                                                        <Typography
                                                                            key={`${row.id}-${index}`}
                                                                            variant="body2"
                                                                            fontWeight={index === 0 ? 700 : 400}
                                                                            color={index === 0 ? '#3D3D3D' : 'text.secondary'}
                                                                        >
                                                                            {value}
                                                                        </Typography>
                                                                    ))}
                                                                </Paper>
                                                            );
                                                        })
                                                        : null}
                                                </Stack>
                                            ) : null}

                                            {sidebarTab === 'assessments' ? (
                                                <Stack spacing={1.2}>
                                                    <Paper sx={compactTableHeaderSx('1fr 1.3fr 1fr 0.7fr')}>
                                                        <Typography variant="caption" fontWeight={600}>Avaliador</Typography>
                                                        <Typography variant="caption" fontWeight={600}>Último Contacto</Typography>
                                                        <Typography variant="caption" fontWeight={600}>Tipo</Typography>
                                                        <Typography variant="caption" fontWeight={600}>Notas</Typography>
                                                    </Paper>

                                                    {sidebarLoadingAssessmentNotes ? (
                                                        <Paper sx={compactTableRowSx('1fr 1.3fr 1fr 0.7fr')}>
                                                            <Typography sx={{ gridColumn: '1 / -1' }} variant="body2" color="text.secondary" textAlign="center">
                                                                A carregar notas...
                                                            </Typography>
                                                        </Paper>
                                                    ) : null}

                                                    {!sidebarLoadingAssessmentNotes && sidebarAssessmentNotes.length === 0 ? (
                                                        <Paper sx={compactTableRowSx('1fr 1.3fr 1fr 0.7fr')}>
                                                            <Typography sx={{ gridColumn: '1 / -1' }} variant="body2" color="text.secondary" textAlign="center">
                                                                Sem notas.
                                                            </Typography>
                                                        </Paper>
                                                    ) : null}

                                                    {!sidebarLoadingAssessmentNotes
                                                        ? sidebarAssessmentNotes.map((note) => (
                                                            <Paper
                                                                key={note?.uuid}
                                                                sx={{
                                                                    p: 1.2,
                                                                    borderRadius: '12px',
                                                                    bgcolor: '#ECECEC',
                                                                    boxShadow: 'none',
                                                                    mb: 0.8,
                                                                    display: 'grid',
                                                                    gridTemplateColumns: '1fr 1.3fr 1fr 0.7fr',
                                                                    gap: 1,
                                                                    alignItems: 'center'
                                                                }}
                                                            >
                                                                <Typography variant="body2" fontWeight={700} color="#3D3D3D">
                                                                    {note?.author?.name || 'Sistema'}
                                                                </Typography>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {formatDateTime(note?.ts || note?.created)}
                                                                </Typography>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {note?.appointment ? 'Consulta' : 'Nota manual'}
                                                                </Typography>
                                                                <Tooltip title="Ver nota">
                                                                    <IconButton
                                                                        size="small"
                                                                        sx={{
                                                                            bgcolor: '#3D3D3D',
                                                                            color: '#fff',
                                                                            width: 34,
                                                                            height: 34,
                                                                            '&:hover': { bgcolor: '#3D3D3D' }
                                                                        }}
                                                                        onClick={() => {
                                                                            void handleOpenSidebarNote(note);
                                                                        }}
                                                                    >
                                                                        <AssignmentOutlinedIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </Paper>
                                                        ))
                                                        : null}

                                                    {notesError ? <Alert severity="error">{notesError}</Alert> : null}
                                                    {notesSuccess ? <Alert severity="success">{notesSuccess}</Alert> : null}

                                                    <TextField
                                                        fullWidth
                                                        multiline
                                                        minRows={5}
                                                        placeholder="Escreva aqui as notas da chamada..."
                                                        value={notes}
                                                        onChange={(e) => setNotes(e.target.value)}
                                                        sx={{
                                                            '& .MuiInputBase-root': {
                                                                alignItems: 'flex-start',
                                                                bgcolor: '#F7F7F7',
                                                                borderRadius: '16px'
                                                            }
                                                        }}
                                                    />
                                                    {canCreatePrivateNotes ? (
                                                        <FormControlLabel
                                                            control={
                                                                <Switch
                                                                    checked={notesPriviledged}
                                                                    onChange={(event) => setNotesPriviledged(event.target.checked)}
                                                                    color="error"
                                                                    disabled={notesSaving}
                                                                />
                                                            }
                                                            label="Nota privada"
                                                            sx={{
                                                                alignSelf: 'flex-start',
                                                                ml: 0
                                                            }}
                                                        />
                                                    ) : null}
                                                    <Stack
                                                        direction={{ xs: 'column', sm: 'row' }}
                                                        spacing={1.2}
                                                        justifyContent="space-between"
                                                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                                                    >
                                                        <Tooltip title="Guardar nota">
                                                            <span>
                                                                <Button
                                                                    variant="contained"
                                                                    onClick={handleSaveNote}
                                                                    disabled={notesSaving}
                                                                    sx={primaryButtonSx}
                                                                >
                                                                    {notesSaving ? 'A guardar...' : 'Guardar'}
                                                                </Button>
                                                            </span>
                                                        </Tooltip>
                                                    </Stack>
                                                </Stack>
                                            ) : null}

                                            {sidebarTab === 'documents' ? (
                                                <Stack spacing={1.1}>
                                                    {sidebarLoadingDocuments ? (
                                                        <Paper sx={{ p: 1.4, borderRadius: '14px', bgcolor: '#ECECEC', boxShadow: 'none' }}>
                                                            <Typography variant="body2" color="text.secondary" textAlign="center">
                                                                A carregar documentos...
                                                            </Typography>
                                                        </Paper>
                                                    ) : null}

                                                    {!sidebarLoadingDocuments && sidebarDocuments.length === 0 ? (
                                                        <Paper sx={{ p: 1.4, borderRadius: '14px', bgcolor: '#ECECEC', boxShadow: 'none' }}>
                                                            <Typography variant="body2" color="text.secondary" textAlign="center">
                                                                Sem documentos.
                                                            </Typography>
                                                        </Paper>
                                                    ) : null}

                                                    {!sidebarLoadingDocuments
                                                        ? sidebarDocuments.map((document) => (
                                                            <Paper
                                                                key={document?.uuid}
                                                                sx={{
                                                                    p: 1.2,
                                                                    borderRadius: '14px',
                                                                    bgcolor: '#ECECEC',
                                                                    boxShadow: 'none',
                                                                    display: 'flex',
                                                                    justifyContent: 'space-between',
                                                                    alignItems: 'center',
                                                                    gap: 1
                                                                }}
                                                            >
                                                                <Box sx={{ minWidth: 0 }}>
                                                                    <Typography variant="body2" fontWeight={700} color="#3D3D3D" noWrap>
                                                                        {document?.name || 'Documento'}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {(document?.tags || []).map((tag) => tag?.name).filter(Boolean).join(', ') || 'Sem tags'}
                                                                    </Typography>
                                                                </Box>
                                                                <Tooltip title="Transferir documento">
                                                                    <Button
                                                                        variant="outlined"
                                                                        sx={secondaryButtonSx}
                                                                        onClick={() => handleDownloadDocument(document?.uuid, document?.name)}
                                                                    >
                                                                        Download
                                                                    </Button>
                                                                </Tooltip>
                                                            </Paper>
                                                        ))
                                                        : null}
                                                </Stack>
                                            ) : null}
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </Card>
                        ) : isPeerTechnicalCallLayout ? (
                            <Box sx={{ minWidth: 0 }}>
                                <Box sx={videoPanelSx}>
                                    {uiState.isLocalVideoEnabled ? (
                                        <video
                                            ref={localVideoRef}
                                            autoPlay
                                            playsInline
                                            muted
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        />
                                    ) : (
                                        <Stack spacing={1.4} alignItems="center">
                                            <VideocamOffIcon sx={{ fontSize: 72, color: '#CFCFCF' }} />
                                            <Typography variant="body1" fontWeight={600} color="#E2E2E2">
                                                A tua câmara está desligada
                                            </Typography>
                                        </Stack>
                                    )}

                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            left: 16,
                                            top: 16,
                                            px: 1.25,
                                            py: 0.7,
                                            borderRadius: '999px',
                                            bgcolor: 'rgba(23, 23, 23, 0.68)',
                                            border: '1px solid rgba(255,255,255,0.12)',
                                            color: '#FFFFFF',
                                            backdropFilter: 'blur(8px)'
                                        }}
                                    >
                                        <Typography variant="body2" fontWeight={700}>
                                            A tua câmara
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        ) : (
                            <Box />
                        )}
                    </Box>

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: '1fr auto 1fr' },
                            alignItems: 'center',
                            gap: 1,
                            pt: 0.25
                        }}
                    >
                        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ ml: 0.25 }}>
                            <Typography variant="h6" fontWeight={700}>
                                {remoteDisplayName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {elapsedClock}
                            </Typography>
                        </Stack>

                        <Stack direction="row" spacing={1.25} justifyContent="center">
                            <Tooltip title={uiState.isLocalAudioEnabled ? 'Desligar microfone' : 'Ligar microfone'}>
                                <IconButton
                                    sx={uiState.isLocalAudioEnabled ? activeControlButtonSx : controlButtonSx}
                                    onClick={() => actions.toggleLocalAudio()}
                                >
                                    {uiState.isLocalAudioEnabled ? <MicIcon /> : <MicOffIcon />}
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={uiState.isLocalVideoEnabled ? 'Desligar câmara' : 'Ligar câmara'}>
                                <IconButton
                                    sx={uiState.isLocalVideoEnabled ? activeControlButtonSx : controlButtonSx}
                                    onClick={() => actions.toggleLocalVideo()}
                                >
                                    {uiState.isLocalVideoEnabled ? <VideocamIcon /> : <VideocamOffIcon />}
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Escolher dispositivos">
                                <IconButton sx={controlButtonSx} onClick={() => setSettingsOpen(true)}>
                                    <SettingsIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={canUseHangupButton ? 'Terminar chamada' : 'A preparar chamada'}>
                                <span>
                                    <IconButton
                                        sx={hangupButtonSx}
                                        disabled={!canUseHangupButton}
                                        onClick={handleEndCall}
                                    >
                                        <CallEndIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Stack>

                        <Box />
                    </Box>
                </Box>
            </Paper>

            <Dialog
                open={sidebarNoteDialogOpen}
                onClose={() => setSidebarNoteDialogOpen(false)}
                fullWidth
                maxWidth="sm"
                PaperProps={{ sx: dialogPaperSx }}
            >
                <DialogTitle sx={dialogTitleSx}>Nota</DialogTitle>
                <DialogContent sx={{ px: 3, py: 1.5 }}>
                    <Stack spacing={1.5}>
                        <Typography variant="body2" color="text.secondary">
                            <strong>Avaliador:</strong> {sidebarSelectedNote?.author?.name || 'Sistema'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            <strong>Data:</strong> {formatDateTime(sidebarSelectedNote?.ts || sidebarSelectedNote?.created)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            <strong>Tipo:</strong> {sidebarSelectedNote?.appointment ? 'Consulta' : 'Nota manual'}
                        </Typography>
                        {sidebarSelectedNoteError ? (
                            <Alert severity="error">{sidebarSelectedNoteError}</Alert>
                        ) : null}
                        <Paper
                            sx={{
                                p: 1.8,
                                borderRadius: '16px',
                                bgcolor: '#F7F7F7',
                                boxShadow: 'none'
                            }}
                        >
                            <Typography variant="body2" color="#3D3D3D" sx={{ whiteSpace: 'pre-wrap' }}>
                                {sidebarLoadingSelectedNote
                                    ? 'A carregar conteúdo...'
                                    : sidebarSelectedNote?.content || 'Sem conteúdo.'}
                            </Typography>
                        </Paper>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3, pt: 0 }}>
                    <Tooltip title="Fechar nota">
                        <Button variant="contained" onClick={() => setSidebarNoteDialogOpen(false)} sx={primaryButtonSx}>
                            Fechar
                        </Button>
                    </Tooltip>
                </DialogActions>
            </Dialog>

            <Dialog
                open={settingsOpen}
                onClose={() => setSettingsOpen(false)}
                PaperProps={{ sx: dialogPaperSx }}
            >
                <DialogTitle sx={dialogTitleSx}>Dispositivos</DialogTitle>
                <DialogContent sx={{ ...dialogContentSx, minWidth: 320 }}>
                    <Stack spacing={2} mt={1}>
                        <FormControl fullWidth>
                            <InputLabel id="audio-select-label">Microfone</InputLabel>
                            <Select
                                labelId="audio-select-label"
                                label="Microfone"
                                value={selectedAudio}
                                onChange={(e) => setSelectedAudio(e.target.value)}
                                sx={{ borderRadius: '14px', bgcolor: '#F7F7F7' }}
                            >
                                {audioDevices.map((d) => (
                                    <MenuItem key={d.deviceId} value={d.deviceId}>
                                        {d.label || 'Microfone'}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth>
                            <InputLabel id="video-select-label">Câmara</InputLabel>
                            <Select
                                labelId="video-select-label"
                                label="Câmara"
                                value={selectedVideo}
                                onChange={(e) => setSelectedVideo(e.target.value)}
                                sx={{ borderRadius: '14px', bgcolor: '#F7F7F7' }}
                            >
                                {videoDevices.map((d) => (
                                    <MenuItem key={d.deviceId} value={d.deviceId}>
                                        {d.label || 'Câmara'}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                </DialogContent>
                <DialogActions sx={dialogActionsSx}>
                    <Tooltip title="Fechar sem guardar">
                        <Button onClick={() => setSettingsOpen(false)} sx={secondaryButtonSx}>
                            Cancelar
                        </Button>
                    </Tooltip>
                    <Tooltip title="Guardar dispositivos selecionados">
                        <Button variant="contained" onClick={handleSaveDevices} sx={primaryButtonSx}>
                            Guardar
                        </Button>
                    </Tooltip>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Call;





